import { useDispatch, useStore } from 'react-redux';
import { useCriarPedidoMutation, type PayloadNovoPedido, type PedidoCriado } from '../api';
import type { RootState } from '../store';
import { assinarEnvio, chaveParaEnvio, descartarEnvioPedido, registrarEnvioPedido } from '../store/carrinho-slice';
import { obterTenantArmazenado } from './tenant';

/**
 * Envio do pedido com chave de idempotência, num lugar só.
 *
 * Toque repetido, erro ambíguo de rede e aba recarregada durante o pagamento já
 * geraram pedido duplicado aqui (a chave nasceu justamente disso). Com o totem
 * virando app próprio, essa regra ou é compartilhada ou passa a existir em duas
 * cópias — e a segunda erra em silêncio, porque duplicar pedido não quebra teste
 * nenhum: aparece como dois pedidos na cozinha.
 *
 * O desfecho volta para a tela decidir o que mostrar; quem cobra antes (TEF e
 * pagamento online) abre o terminal e só conclui depois da aprovação.
 */

export type DesfechoEnvio =
  /** O pedido reaproveitado já estava cancelado: a chave foi descartada, refazer gera outro. */
  | { tipo: 'cancelado' }
  /** Nasceu aguardando pagamento e a forma cobra antes: abra o terminal/pagamento. */
  | { tipo: 'cobrar'; pedido: PedidoCriado }
  /** Pode limpar o carrinho e ir para o pedido. */
  | { tipo: 'concluido'; pedido: PedidoCriado };

export interface EnvioDoPedido {
  payload: PayloadNovoPedido;
  /** Id do usuário logado, quando há (delivery); entra na assinatura da chave. */
  idUsuario?: number | string | null;
  /** TEF e pagamento online cobram ANTES de o pedido ser confirmado. */
  cobraAntes: boolean;
  /** Erro em que a chave não serve mais (ex.: cashback com a chave encerrada): reenvia com chave nova, uma vez só. */
  trocarChaveSe?: (err: unknown) => boolean;
  /** Recebe a chave de cada tentativa: quem trata o erro pode precisar dela. */
  aoUsarChave?: (chave: string) => void;
}

export function useEnviarPedido(): {
  enviar: (envio: EnvioDoPedido) => Promise<DesfechoEnvio>;
  descartarChave: () => void;
  enviando: boolean;
} {
  const dispatch = useDispatch();
  const store = useStore<RootState>();
  const [criarPedido, status] = useCriarPedidoMutation();

  async function enviar({ payload, idUsuario = null, cobraAntes, trocarChaveSe, aoUsarChave }: EnvioDoPedido): Promise<DesfechoEnvio> {
    // A chave é lida do store AGORA (não do render): um erro ambíguo pode ter
    // gravado o pedido, e a próxima tentativa precisa mandar a MESMA chave.
    const chaveDoEnvio = (): string => {
      const assinatura = assinarEnvio({ tenant: obterTenantArmazenado(), usuario: idUsuario, payload });
      const envio = chaveParaEnvio(store.getState().carrinho.envioPedido, assinatura);
      if (envio.nova) dispatch(registrarEnvioPedido({ chave: envio.chave, assinatura }));
      aoUsarChave?.(envio.chave);
      return envio.chave;
    };

    let pedido: PedidoCriado;
    try {
      pedido = await criarPedido({ ...payload, chaveIdempotencia: chaveDoEnvio() }).unwrap();
    } catch (err) {
      if (!trocarChaveSe?.(err)) throw err;
      dispatch(descartarEnvioPedido());
      pedido = await criarPedido({ ...payload, chaveIdempotencia: chaveDoEnvio() }).unwrap();
    }

    // A chave reusada pode devolver pedido já cancelado: sem isto o terminal
    // passaria o cartão antes de o backend recusar.
    if (cobraAntes && pedido.situacao === 'CANCELADO') {
      dispatch(descartarEnvioPedido());
      return { tipo: 'cancelado' };
    }

    // Só cobra o que ainda aguarda pagamento; o já pago (aba recarregada durante
    // o PIX) segue como concluído.
    if (cobraAntes && pedido.situacao === 'AGUARDANDO_PAGAMENTO') return { tipo: 'cobrar', pedido };

    return { tipo: 'concluido', pedido };
  }

  return {
    enviar,
    descartarChave: () => dispatch(descartarEnvioPedido()),
    enviando: status.isLoading,
  };
}
